<script setup lang="ts">
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {ChevronLeft, Clock, HeartHandshake, ScanQrCode, Send, UserSearch} from "@lucide/vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseAlert from "@/core/components/base/BaseAlert.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Button} from "@/core/components/ui/button";
import {useToastStore} from "@/stores/toast.store";
import BirthdayTodayTable from "@/birthday-gift/components/BirthdayTodayTable.vue";
import GiftAutoSummaryCard from "@/birthday-gift/components/GiftAutoSummaryCard.vue";
import GiftManualSendDialog from "@/birthday-gift/components/GiftManualSendDialog.vue";
import GiftScanDialog from "@/birthday-gift/components/scan/GiftScanDialog.vue";
import useBirthdayGifts from "@/birthday-gift/composables/useBirthdayGifts";
import useBirthdayGiftSettings from "@/birthday-gift/composables/useBirthdayGiftSettings";
import useTodayBirthdays from "@/birthday-gift/composables/useTodayBirthdays";
import {birthdayGiftApi} from "@/container";
import {BLOCKED_REASON_LABELS, giftProfilesOverlap} from "@/birthday-gift/constants/palettes";
import {GiftSendHour, TodayBirthdayVoucher} from "@/birthday-gift/interfaces";

const router = useRouter();
const toast = useToastStore();

const {data: vouchers, isLoading, mutate: refreshVouchers} = useTodayBirthdays();
const {data: gifts} = useBirthdayGifts();
const {data: settings} = useBirthdayGiftSettings();

const pendingIds = ref<number[]>([]);
const sending = ref(false);
const sentCount = ref(0);
const sendIssues = ref<string | null>(null);
const scanOpen = ref(false);
const manualSendOpen = ref(false);

const sendHour = computed<GiftSendHour>(() => settings.value?.send_hour ?? 9);
const hourLabel = computed(() => `${String(sendHour.value).padStart(2, '0')}:00`);

const activeGifts = computed(() => (gifts.value ?? []).filter((gift) => gift.active));

/** Two automatic gifts targeting the same profile suspend every automatic send. */
const conflictingGifts = computed(() => {
    const auto = activeGifts.value.filter((gift) => gift.auto_send);

    return auto.filter((gift, index) => auto
        .slice(index + 1)
        .some((other) => giftProfilesOverlap(gift, other)));
});

const toValidate = computed(() => (vouchers.value ?? [])
    .filter((voucher) => voucher.status === 'pending_validation'));

const counts = computed(() => {
    const list = vouchers.value ?? [];

    return {
        scheduled: list.filter((voucher) => voucher.status === 'scheduled').length,
        toValidate: toValidate.value.length,
        // A redeemed voucher was sent too: both count as « envoyé » here.
        sent: list.filter((voucher) => ['sent', 'redeemed'].includes(voucher.status)).length,
        excluded: list.filter((voucher) => voucher.status === 'excluded').length,
    };
});

/**
 * The intro follows the state of the day: still to come, partly done, or over.
 */
const intro = computed(() => {
    const {scheduled, toValidate, sent} = counts.value;

    if (scheduled === 0 && toValidate === 0) {
        return sent > 0
            ? 'Les messages du jour sont partis : vous pouvez consulter ci-dessous qui a reçu quel cadeau.'
            : 'Aucun message à envoyer aujourd\'hui.';
    }

    const parts: string[] = [];

    if (sent > 0) {
        parts.push(`${sent} message(s) sont déjà partis.`);
    }

    if (scheduled > 0) {
        parts.push(
            `Les cadeaux en envoi automatique partiront seuls à ${hourLabel.value} : vous pouvez `
            + 'encore changer un cadeau ou exclure un patient jusqu\'à cette heure.'
        );
    }

    if (toValidate > 0) {
        parts.push(`${toValidate} message(s) sont à valider ici.`);
    }

    return parts.join(' ');
});

/**
 * Run a voucher mutation, keeping the row disabled until the server answered.
 */
const mutateVoucher = async (voucherId: number, action: () => Promise<unknown>, failure: string) => {
    pendingIds.value = [...pendingIds.value, voucherId];

    try {
        await action();
        await refreshVouchers();
    } catch {
        toast.showError({content: failure});
    } finally {
        pendingIds.value = pendingIds.value.filter((id) => id !== voucherId);
    }
};

const toggleExclusion = (voucher: TodayBirthdayVoucher, excluded: boolean) => mutateVoucher(
    voucher.id,
    () => birthdayGiftApi().updateVoucher(voucher.id, {excluded}),
    'Le patient n\'a pas pu être mis à jour.'
);

const overrideGift = (voucher: TodayBirthdayVoucher, giftId: number) => mutateVoucher(
    voucher.id,
    () => birthdayGiftApi().updateVoucher(voucher.id, {birthday_gift_id: giftId}),
    'Le cadeau n\'a pas pu être changé.'
);

/**
 * Send every message still awaiting the pharmacist validation.
 */
const sendPending = async () => {
    sending.value = true;
    sentCount.value = 0;
    sendIssues.value = null;

    const results = await Promise.allSettled(
        toValidate.value.map((voucher) => birthdayGiftApi().sendVoucher(voucher.id))
    );

    // The endpoint answers 200 even when the service blocked the send (no
    // credit, gift paused…), so the returned status is what decides.
    const fulfilled = results
        .filter((result): result is PromiseFulfilledResult<TodayBirthdayVoucher> =>
            result.status === 'fulfilled')
        .map((result) => result.value);

    const blocked = fulfilled.filter((voucher) => voucher.status !== 'sent');
    const failed = results.length - fulfilled.length;

    sentCount.value = fulfilled.length - blocked.length;
    sendIssues.value = describeIssues(blocked, failed);

    await refreshVouchers();
    sending.value = false;
};

/**
 * Explain, per reason, the messages that did not go out.
 */
const describeIssues = (blocked: TodayBirthdayVoucher[], failed: number): string | null => {
    const parts: string[] = [];

    const byReason = blocked.reduce<Record<string, number>>((counts, voucher) => {
        const label = voucher.blocked_reason
            ? BLOCKED_REASON_LABELS[voucher.blocked_reason]
            : 'Bloqué';

        return {...counts, [label]: (counts[label] ?? 0) + 1};
    }, {});

    Object.entries(byReason).forEach(([label, count]) => {
        parts.push(`${count} — ${label.toLowerCase()}`);
    });

    if (failed) {
        parts.push(`${failed} — erreur technique`);
    }

    return parts.length ? parts.join(' · ') : null;
};
</script>

<template>
  <BaseContainer class="p-4">
    <Button
      variant="link"
      class="self-start px-0"
      @click="router.push({name: 'patient-administration.communication.birthday-gifts'})"
    >
      <ChevronLeft class="h-4 w-4" />Tous les cadeaux
    </Button>

    <div class="flex flex-wrap items-start justify-between gap-3">
      <h1 class="mb-1 mt-1 text-2xl font-medium">
        Anniversaires du jour
      </h1>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          class="rounded-full border-solid"
          @click="manualSendOpen = true"
        >
          <UserSearch class="h-4 w-4" />Envoyer à un patient
        </Button>
        <Button
          class="rounded-full bg-secondary text-primary-dark hover:bg-secondary-dark"
          @click="scanOpen = true"
        >
          <ScanQrCode class="h-4 w-4" />Scanner un QR code
        </Button>
      </div>
    </div>
    <p class="max-w-[720px] text-sm leading-relaxed text-muted-foreground">
      {{ intro }}
    </p>

    <div class="mt-3 inline-flex max-w-[720px] items-start gap-2 rounded-lg bg-primary-lightest px-3 py-2.5 text-xs leading-relaxed">
      <HeartHandshake class="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      Seuls les patients fidèles sont proposés : ceux venus au moins deux fois à la pharmacie sur
      les douze derniers mois, et majeurs.
    </div>

    <BaseAlert
      v-if="conflictingGifts.length"
      class="mt-4"
      type="error"
      title="Envoi automatique bloqué — profils en conflit"
    >
      « {{ conflictingGifts[0].name }} » cible le même profil qu'un autre cadeau automatique.
      Un même patient recevrait deux messages : corrigez les profils ou désactivez l'envoi
      automatique de l'un des deux.
    </BaseAlert>

    <BaseAlert
      v-if="sentCount > 0"
      class="mt-4"
      type="success"
      title="Messages envoyés"
    >
      {{ sentCount }} message(s) RCS partis avec leur QR code. Les cadeaux apparaîtront dans les
      statistiques dès le premier scan au comptoir.
    </BaseAlert>

    <BaseAlert
      v-if="sendIssues"
      class="mt-4"
      type="error"
      title="Certains messages ne sont pas partis"
    >
      {{ sendIssues }}. Les patients concernés restent dans la liste ci-dessous avec la raison.
    </BaseAlert>

    <GiftAutoSummaryCard
      class="mt-4"
      :gifts="gifts ?? []"
      :send-hour="sendHour"
      @open-today="refreshVouchers"
    />

    <BaseFlexSpinner v-if="isLoading" />
    <div
      v-else
      class="mt-4 overflow-hidden rounded-lg bg-white shadow"
    >
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-light px-5 py-3.5">
        <h2 class="text-lg font-medium">
          {{ (vouchers ?? []).length }} patients fêtent leur anniversaire
        </h2>
        <span class="text-xs text-muted-foreground">Patient · cadeau proposé · statut</span>
      </div>

      <BirthdayTodayTable
        :vouchers="vouchers ?? []"
        :gifts="activeGifts"
        :send-hour="sendHour"
        :pending-ids="pendingIds"
        @toggle-exclusion="toggleExclusion"
        @override-gift="overrideGift"
      />

      <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
        <span class="text-xs text-muted-foreground">
          <template v-if="counts.sent">{{ counts.sent }} envoyé(s) · </template>
          <template v-if="counts.scheduled">{{ counts.scheduled }} programmé(s) · </template>
          {{ counts.toValidate }} à valider · {{ counts.excluded }} exclu(s)
        </span>

        <Button
          v-if="counts.toValidate > 0"
          :disabled="sending"
          @click="sendPending"
        >
          <Send class="h-4 w-4" />Envoyer les {{ counts.toValidate }} message(s) à valider
        </Button>
        <span
          v-else-if="counts.scheduled > 0"
          class="flex items-center gap-2 text-xs font-semibold text-primary"
        >
          <Clock class="h-4 w-4" />Envoi prévu à {{ hourLabel }}
        </span>
      </div>
    </div>

    <GiftScanDialog
      v-model:open="scanOpen"
      @redeemed="refreshVouchers"
    />

    <GiftManualSendDialog
      v-model:open="manualSendOpen"
      :gifts="activeGifts"
      @sent="refreshVouchers"
    />
  </BaseContainer>
</template>
